/**
 * @file Shared geometry/material cache plus per-prefab mesh pools.
 *
 * Two GDD §19 requirements land here: never duplicate a geometry or a material,
 * and never allocate a mesh in the middle of a frame. Callers ask for a prefab
 * by key; the factory hands back a recycled `THREE.Mesh` whose geometry and
 * material are shared instances.
 */

import * as THREE from 'three';
import { ObjectPool } from '../utils/ObjectPool.js';

/**
 * @typedef {object} PrefabDefinition
 * @property {() => THREE.BufferGeometry} geometry Builds the shared geometry.
 * @property {() => THREE.Material} material Builds the shared material.
 * @property {() => THREE.Object3D | null} [model] Builds an authored model
 *   instead of the geometry/material pair. Returning null falls back to them,
 *   which is how a prefab keeps working when its model failed to download.
 * @property {boolean} [castShadow] Defaults to true.
 * @property {boolean} [receiveShadow] Defaults to false.
 * @property {number} [prewarm] Meshes to allocate up front.
 * @property {(object: THREE.Object3D, factory: MeshFactory) => void} [decorate] Optional
 *   hook to attach child meshes (barrels, eyes) once, at build time. Children
 *   reuse cached geometries/materials just like the root object.
 */

export class MeshFactory {
  constructor() {
    /** @type {Map<string, THREE.BufferGeometry>} @private */
    this._geometries = new Map();
    /** @type {Map<string, THREE.Material>} @private */
    this._materials = new Map();
    /** @type {Map<string, PrefabDefinition>} @private */
    this._prefabs = new Map();
    /** @type {Map<string, ObjectPool<THREE.Mesh>>} @private */
    this._pools = new Map();
  }

  /**
   * Returns a cached geometry, building it on first request.
   * @param {string} key Cache key.
   * @param {() => THREE.BufferGeometry} build Factory used on cache miss.
   * @returns {THREE.BufferGeometry}
   */
  geometry(key, build) {
    let geometry = this._geometries.get(key);
    if (!geometry) {
      geometry = build();
      this._geometries.set(key, geometry);
    }
    return geometry;
  }

  /**
   * Returns a cached material, building it on first request.
   * @param {string} key Cache key.
   * @param {() => THREE.Material} build Factory used on cache miss.
   * @returns {THREE.Material}
   */
  material(key, build) {
    let material = this._materials.get(key);
    if (!material) {
      material = build();
      this._materials.set(key, material);
    }
    return material;
  }

  /**
   * Declares a poolable prefab. Call once at load time.
   * @param {string} key Prefab name.
   * @param {PrefabDefinition} definition How to build it.
   * @returns {void}
   */
  definePrefab(key, definition) {
    this._prefabs.set(key, definition);

    const pool = new ObjectPool(
      () => this._buildMesh(key, definition),
      (mesh) => {
        mesh.removeFromParent();
        mesh.visible = false;
        mesh.scale.set(1, 1, 1);
        mesh.rotation.set(0, 0, 0);
      },
      definition.prewarm ?? 0
    );
    this._pools.set(key, pool);
  }

  /**
   * @param {string} key Prefab name.
   * @param {PrefabDefinition} definition Prefab definition.
   * @returns {THREE.Object3D}
   * @private
   */
  _buildMesh(key, definition) {
    const castShadow = definition.castShadow ?? true;
    const receiveShadow = definition.receiveShadow ?? false;

    // Model-backed prefabs are built lazily on first acquire, which is what
    // lets a prefab be declared while its `.glb` is still downloading.
    const model = definition.model?.() ?? null;
    const object =
      model ??
      new THREE.Mesh(
        this.geometry(key, definition.geometry),
        this.material(key, definition.material)
      );

    object.name = key;
    object.castShadow = castShadow;
    object.receiveShadow = receiveShadow;
    object.matrixAutoUpdate = true;
    if (model) {
      model.traverse((node) => {
        if (!node.isMesh) return;
        node.castShadow = castShadow;
        node.receiveShadow = receiveShadow;
      });
    }
    definition.decorate?.(object, this);
    return object;
  }

  /**
   * Borrows a mesh for a prefab. The mesh is hidden until a render system
   * attaches it to the scene.
   * @param {string} key Prefab name declared with {@link MeshFactory#definePrefab}.
   * @returns {THREE.Object3D}
   */
  acquire(key) {
    const pool = this._pools.get(key);
    if (!pool) throw new ReferenceError(`Unknown prefab "${key}". Call definePrefab first.`);
    const mesh = pool.acquire();
    mesh.visible = true;
    return mesh;
  }

  /**
   * Returns a mesh to its pool.
   * @param {string} key Prefab name.
   * @param {THREE.Object3D} mesh Object previously acquired.
   * @returns {void}
   */
  release(key, mesh) {
    this._pools.get(key)?.release(mesh);
  }

  /**
   * Frees every cached GPU resource.
   * @returns {void}
   */
  dispose() {
    for (const pool of this._pools.values()) pool.clear((mesh) => mesh.removeFromParent());
    this._pools.clear();
    for (const geometry of this._geometries.values()) geometry.dispose();
    this._geometries.clear();
    for (const material of this._materials.values()) material.dispose();
    this._materials.clear();
    this._prefabs.clear();
  }
}
